import { type Api } from '../api/types'

/** Как способ отгрузки и площадка называются в интерфейсе. */
export const shipmentTypeLabels: Record<Api.ShipmentType, string> = {
  fbs: 'FBS',
  fbo: 'FBO',
  dbs: 'DBS',
  other: 'Другое',
}

/** Порядок схем работы: один и тот же в форме владельца, в прайсе карточки и в теле запроса. */
export const shipmentTypeOrder = ['fbs', 'fbo', 'dbs', 'other'] as const satisfies readonly Api.ShipmentType[]

/** Короткие подписи для тегов и фильтров: рядом стоит логотип, поэтому полное название не нужно. */
export const marketplaceLabels: Record<Api.Marketplace, string> = {
  wildberries: 'WB',
  ozon: 'Ozon',
  yandex_market: 'Я.Маркет',
  magnit_market: 'Магнит',
  other: 'Другой',
}

/**
 * Услуги, которые продавец отмечает в заявке на просчёт; порядок задаёт вывод в мастере заявки.
 * К прайсу карточки перечень отношения не имеет — туда услуги приходят справочником с сервера.
 *
 * Подписи наши: с сервера приходят только коды. Держим их теми же словами, что у бэкенда
 * (перечисление `FulfillmentServiceType`), иначе один и тот же код будет называться по-разному
 * в разных местах продукта, и никакой ошибки при этом не возникнет — разъедется молча.
 */
export const serviceTypes = [
  { code: 'pickup_from_supplier', label: 'Забор груза у поставщика' },
  { code: 'acceptance', label: 'Приёмка' },
  { code: 'defect_check', label: 'Проверка на брак' },
  { code: 'steaming', label: 'Отпаривание и глажка' },
  { code: 'labeling', label: 'Маркировка' },
  { code: 'insert_and_manual', label: 'Вкладыш и инструкция' },
  { code: 'packaging', label: 'Упаковка' },
  { code: 'oversized_packaging', label: 'Упаковка КГТ' },
  { code: 'pallets', label: 'Паллетирование' },
  { code: 'storage', label: 'Хранение' },
  { code: 'delivery_to_warehouse', label: 'Доставка до склада маркетплейса' },
  { code: 'fbs_shipment', label: 'Отгрузка на СЦ' },
  { code: 'dbs_shipment', label: 'Доставка до ПВЗ' },
] as const satisfies readonly { code: Api.ServiceType; label: string }[]

/**
 * Пометка состояния карточки для её владельца. `null` — у опубликованной: она видна в каталоге,
 * и объяснять нечего. `variant` — окраска плашки: `warning` там, где от владельца ждут действия.
 */
export const myFulfillmentStatusBadges = {
  pending: { label: 'На проверке', variant: 'info' },
  published: null,
  rejected: { label: 'Вернули на доработку', variant: 'warning' },
  hidden: { label: 'Скрыта администратором', variant: 'secondary' },
} as const satisfies Record<
  Api.MyFulfillmentStatus,
  { label: string; variant: 'info' | 'warning' | 'secondary' } | null
>

/** Как часто фулфилмент возит товар на склады маркетплейса — от самого быстрого к самому редкому. */
export const deliveryFrequencies = [
  { code: 'twice_a_day', label: 'Дважды в день' },
  { code: 'once_a_day', label: 'Раз в день' },
  { code: 'several_times_a_week', label: 'Несколько раз в неделю (по графику)' },
  { code: 'rarely', label: 'Реже (по договорённости)' },
] as const satisfies readonly { code: Api.DeliveryFrequency; label: string }[]

/** Подписи для единиц измерения, в которых указываются цены услуг. */
export const serviceUnitLabels: Record<Api.ServiceUnit, string> = {
  item: 'шт.',
  box: 'короб',
  pallet: 'паллета',
  pallet_day: 'паллета в сутки',
  trip: 'поездка',
}

/** Подписи для единиц объёма в лесенке пакета «под ключ». */
export const volumeUnitLabels: Record<Api.VolumeUnit, string> = {
  items_per_supply: 'шт. в поставке',
  orders_per_day: 'заказов в день',
}

/**
 * В чём меряется объём в лестнице цен. В самой строке прайса единицы нет: она следует из схемы
 * работы — у FBO это единицы товара в поставке, у FBS и DBS заказы в день. У «другого» лестницы
 * не бывает, поэтому и подписи нет.
 */
export const ladderVolumeUnitLabels: Record<Api.ShipmentType, string | null> = {
  fbs: volumeUnitLabels.orders_per_day,
  fbo: volumeUnitLabels.items_per_supply,
  dbs: volumeUnitLabels.orders_per_day,
  other: null,
}
